import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import memoize from 'memoize-one';
import {
  Notification,
  NotificationType,
  IconTooltip,
  InfoIcon,
  Box
} from 'edenred-ui';
import { useFormContext } from 'react-hook-form';

import { VoucherQuantityField } from '@epi-forms/controls/VoucherQuantityField';
import { FaceValueSelect } from '@epi-forms/controls/FaceValueSelect';
import { FormFields } from '@epi-forms/helpers';
import {
  IFilteredVouchers,
  VoucherQuantityProps
} from '@epi-models/forms/VoucherQuantity';
import { IVouchers } from '@epi-models/containers/OrderAmount';

import {
  maxValue,
  maxVouchersValue
} from '../../helpers/formHelpers/validators';
import { formatCurrency } from '../../helpers/numeral';
import { VoucherTypes } from '../../constants/voucherTypes';
import { BenefitCalculator } from '../BenefitCalculator/BenefitCalculator';
import voucherLunchImage from '../../../assets/images/LunchVoucher.png';
import voucherRecreationalImage from '../../../assets/images/RecreationalVoucher.png';

import './VoucherQuantity.scss';

function VoucherQuantity({
  voucherMinimumQuantity: minQtyValue,
  vouchers,
  voucherTypeKey,
  locale,
  useCalculator = false,
  newFaceValueMessage,
  maxOrderTotalAmount
}: VoucherQuantityProps) {
  const { t } = useTranslation();
  const [showAdvice, setShowAdvice] = useState(false);
  const [isRounded, setIsRounded] = useState(false);
  const [filteredVouchers, setFilteredVouchers] = useState<IFilteredVouchers[]>(
    []
  );
  const [maxValueTotalAmountValidator, setMaxValueTotalAmountValidator] =
    useState(() => maxValue(50000));

  const { watch, setValue, trigger } = useFormContext();
  const quantityValue = watch(FormFields.VoucherQuantity);
  const voucherIdValue = watch(FormFields.VoucherId);
  const voucherWorthValue = watch(FormFields.VoucherWorth);

  const calculateVoucherValues = memoize((vouchers: IVouchers[]) => {
    if (!vouchers || vouchers.length === 0) {
      return;
    }

    const getFilteredVouchers = vouchers.map(item => ({
      ...item,
      value: item.id,
      faceValue: item.value,
      label: `${formatCurrency(item.value)}`
    }));

    setFilteredVouchers(getFilteredVouchers);

    if (!voucherIdValue)
      setValue(FormFields.VoucherId, getFilteredVouchers[0].value);
  });

  const getOrderSum = () => {
    return quantityValue * voucherWorthValue;
  };

  const setVoucherRoundedQuantity = value => {
    const valueAsNumber = Number(value);
    const roundedValue = Number(roundVoucherQty(value));
    setIsRounded(roundedValue !== valueAsNumber);
    if (roundedValue !== valueAsNumber || quantityValue !== valueAsNumber) {
      setValue(FormFields.VoucherQuantity, roundedValue);
    }
    trigger();
  };

  const setVoucherQuantity = voucherQty => {
    setShowAdvice(true);
    setVoucherRoundedQuantity(voucherQty);
  };

  const handleQuantityOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setShowAdvice(false);
    setIsRounded(false);
    e.preventDefault();
  };

  const maxAllowedQuantity = () => {
    if (
      !quantityValue ||
      !voucherIdValue ||
      !vouchers ||
      vouchers.length === 0
    ) {
      return;
    }
    const maxVoucherQty =
      Math.floor(maxOrderTotalAmount / voucherWorthValue / 10) * 10;

    setMaxValueTotalAmountValidator(() => maxVouchersValue(maxVoucherQty));
  };

  const roundVoucherQty = value => {
    const maxQtyValue = 50000;

    if (isNaN(value)) {
      return minQtyValue;
    }
    if (value >= maxQtyValue) {
      return maxQtyValue;
    }
    if (value <= minQtyValue) {
      return minQtyValue;
    }
    if (value % 10 !== 0) {
      return Math.ceil(value / 10) * 10;
    }
    return value;
  };

  const handleQuantityOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setShowAdvice(false);
    setVoucherRoundedQuantity(e.target.value);
    e.preventDefault();
  };

  const closeRoundedNotification = () => {
    setIsRounded(false);
  };

  const closeAdviceNotification = () => {
    setShowAdvice(false);
  };

  useEffect(() => {
    calculateVoucherValues(vouchers);
    maxAllowedQuantity();
  }, []);

  useEffect(() => {
    maxAllowedQuantity();
    if (quantityValue && voucherIdValue) trigger();
  }, [quantityValue, voucherIdValue]);

  useEffect(() => {
    calculateVoucherValues(vouchers);

    if (vouchers.length > 0 && !voucherIdValue) {
      setValue(FormFields.VoucherId, vouchers[0].id);
    }
  }, [vouchers]);

  useEffect(() => {
    setValue(
      FormFields.VoucherWorth,
      (vouchers || []).find(v => v.id === voucherIdValue)?.value
    );
  }, [vouchers, voucherIdValue]);

  const sumVouchers = getOrderSum();
  const tooltipText =
    t(
      `forms.benefit_calculator.info_tooltip[${voucherTypeKey.toLowerCase()}]`
    ) || '';
  return (
    <div className="VoucherQuantity">
      <Box gap={5}>
        {(useCalculator && (
          <Box>
            <BenefitCalculator
              tooltip={tooltipText}
              locale={locale}
              handleSetQuantityValue={setVoucherQuantity}
              maxOrderTotalAmount={maxOrderTotalAmount}
            />
          </Box>
        )) || (
          <Box display="flex" justifyContent="flex-end">
            <IconTooltip title={tooltipText}>
              <InfoIcon />
            </IconTooltip>
          </Box>
        )}
        <Box display="flex" gap={3}>
          <Box flex={1}>
            <VoucherQuantityField
              label={t(
                `forms.voucher_quantity.voucher_name[${voucherTypeKey.toLowerCase()}]`
              )}
              onFocus={handleQuantityOnFocus}
              onBlur={handleQuantityOnBlur}
              validate={[maxValueTotalAmountValidator]}
            />
          </Box>
          <Box flex={1}>
            <FaceValueSelect filteredVouchers={filteredVouchers} />
          </Box>
          <Box flex={1}>
            <img
              className="vertical-center voucher-image"
              alt="Voucher"
              src={
                VoucherTypes[voucherTypeKey] !== VoucherTypes.Lunch
                  ? voucherRecreationalImage
                  : voucherLunchImage
              }
            />
          </Box>
          <Box flex={1} display="flex" justifyContent="flex-end" pt={2} pr={8}>
            <p className="amount">
              <span className="header3 small text-right pull-right">
                = {formatCurrency(sumVouchers)}
              </span>
            </p>
          </Box>
        </Box>
        {isRounded && (
          <Notification
            text={t('forms.voucher_quantity.rounded_value')}
            type={NotificationType.Error}
            onClose={closeRoundedNotification}
          />
        )}
        {newFaceValueMessage && (
          <Notification
            text={newFaceValueMessage}
            type={NotificationType.Important}
          />
        )}
        {showAdvice && (
          <Notification
            text={t('forms.voucher_quantity.adviced_value')}
            type={NotificationType.Info}
            onClose={closeAdviceNotification}
          />
        )}
      </Box>
    </div>
  );
}

const VoucherQuantityWithI18n = connect()(VoucherQuantity);

export { VoucherQuantityWithI18n as VoucherQuantity };
